// apps/web/src/pages/spread/SpreadComposerToolbar.tsx
//
// Phase 9 Plan 11 Task 2 — top-level toolbar.
//
// Visual contract per UI-SPEC §"Spread Composer toolbar" lines 343-355:
//   - Toolbar title: `{spread.name}` or `New {container}` (per useTerm).
//   - Save button: `Save {container}` (primary variant — the ONLY default
//     variant in the SpreadComposer chrome). aria-label is stable across
//     states; copy changes between "Save", "Saved", "Saving…", "Save" + AlertCircle.
//   - Settings + Share buttons: `variant="outline"`.
//
// Per-path useWatch — title via the parent's prop. Toolbar itself does NOT
// reach into form state (mirrors Phase 8 EditorToolbarWrapper pattern).

import { AlertCircle, Check, Loader2, Settings as SettingsIcon, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTerm } from "@/lib/vocabulary/useTerm";

export interface SpreadComposerToolbarProps {
  /** Spread name (rendered as toolbar title). Caller wires this via useWatch. */
  spreadName: string | undefined;
  /** Whether the form is dirty (i.e., the save button should activate). */
  isDirty: boolean;
  /** Whether the save mutation is currently in-flight. */
  isSaving: boolean;
  /** Whether the form is currently valid. */
  isValid: boolean;
  /** Click handler for Save. */
  onSave: () => void;
  /** Click handler for Settings (opens SpreadSettingsDialog — Plan 09-12). */
  onOpenSettings: () => void;
  /** Click handler for Share (opens Sharing tab — Plan 09-12). */
  onOpenShare: () => void;
}

export function SpreadComposerToolbar(props: SpreadComposerToolbarProps): React.ReactElement {
  const container = useTerm("container");
  const titleText =
    typeof props.spreadName === "string" && props.spreadName.length > 0
      ? props.spreadName
      : `New ${container}`;

  // Save button state machine — UI-SPEC §"Spread Composer toolbar" lines
  // 346-352. Aria-label is stable across all states (line 352).
  const ariaLabelSave = `Save ${container}`;

  let saveLabel: React.ReactNode;
  let saveDisabled: boolean;

  if (props.isSaving) {
    saveLabel = (
      <>
        <Loader2 size={14} className="animate-spin" aria-hidden /> Saving…
      </>
    );
    saveDisabled = true;
  } else if (!props.isDirty) {
    saveLabel = (
      <>
        <Check size={14} aria-hidden className="text-muted-foreground" /> Saved
      </>
    );
    saveDisabled = true;
  } else if (!props.isValid) {
    saveLabel = (
      <>
        <AlertCircle size={14} aria-hidden /> Save {container}
      </>
    );
    saveDisabled = true;
  } else {
    saveLabel = <>Save {container}</>;
    saveDisabled = false;
  }

  return (
    <div
      role="toolbar"
      aria-label={`${container} composer toolbar`}
      className="flex items-center justify-between border-b border-border bg-background px-4 py-3"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-semibold leading-snug">{titleText}</h2>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={props.onOpenSettings}>
          <SettingsIcon size={14} aria-hidden /> Settings
        </Button>
        <Button type="button" variant="outline" onClick={props.onOpenShare}>
          <Share2 size={14} aria-hidden /> Share
        </Button>
        <Button
          type="button"
          variant="default"
          aria-label={ariaLabelSave}
          disabled={saveDisabled}
          onClick={props.onSave}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
SpreadComposerToolbar.displayName = "SpreadComposerToolbar";
